// BDSJEA-341: funciones puras para presentar el estado de la Importación Masiva.
// El contrato (GET /api/agro/migracion/estado) lo define el backend; todo se
// lee de forma defensiva porque el migrador puede mandar null en cualquier campo.
import { formatearNumero } from "helpers/formatters"

export const ESTADO = {
  INICIANDO: "INICIANDO",
  EN_CURSO: "EN_CURSO",
  COMPLETADA: "COMPLETADA",
  COMPLETADA_CON_EXCLUIDOS: "COMPLETADA_CON_EXCLUIDOS",
  ERROR: "ERROR",
  INTERRUMPIDA: "INTERRUMPIDA",
  SIN_RESPUESTA: "SIN_RESPUESTA",
  SIN_ARRANQUE: "SIN_ARRANQUE",
  DESCONOCIDO: "DESCONOCIDO",
  SIN_CORRIDAS: "SIN_CORRIDAS",
}

// Estados finales: detienen el polling y llevan a la fase COMPLETADO.
export const ESTADOS_FINALES = [
  ESTADO.COMPLETADA,
  ESTADO.COMPLETADA_CON_EXCLUIDOS,
  ESTADO.ERROR,
  ESTADO.INTERRUMPIDA,
]

// Estados de alerta que NO son finales: el polling sigue (lento) pero el modal
// se desbloquea para que el usuario pueda cerrarlo.
export const ESTADOS_DESBLOQUEO = [
  ESTADO.SIN_RESPUESTA,
  ESTADO.SIN_ARRANQUE,
  ESTADO.DESCONOCIDO,
]

export const esEstadoFinal = estadoEfectivo =>
  ESTADOS_FINALES.includes(estadoEfectivo)

export const esEstadoDesbloqueo = estadoEfectivo =>
  ESTADOS_DESBLOQUEO.includes(estadoEfectivo)

// Segundos sin ninguna noticia del proceso (ni estado ni log) tras los cuales
// se informa un error en lugar de seguir en "Iniciando" (= arranque-seg del BE).
export const UMBRAL_SIN_NOTICIAS_SEG = 90

/**
 * Si el estado recibido es el de la corrida ANTERIOR (ya terminada, la misma
 * que había al abrir el modal) o no hay corridas todavía, el proceso nuevo
 * aún no generó su estado: se lo trata como INICIANDO.
 */
export const resolverEstado = (estado, corridaPrevia) => {
  if (!estado || typeof estado !== "object") return null
  const ef = estado.estadoEfectivo
  const esAnterior =
    corridaPrevia != null &&
    estado.corrida === corridaPrevia &&
    esEstadoFinal(ef)
  if (esAnterior || ef === ESTADO.SIN_CORRIDAS) {
    return { ...estado, estadoEfectivo: ESTADO.INICIANDO, mensaje: null }
  }
  return estado
}

const NOMBRES_CORTOS = {
  importar_productores: "Productores",
  importar_establecimientos: "Establecimientos",
  sincronizar_arca: "ARCA",
  crear_establecimientos_sin_detalle: "Establecimientos sin detalle",
  // BDSJEA-353: etapa previa de la limpieza completa de la base.
  limpiar_base_completa: "Limpieza completa de la base",
  // BDSJEA-441: pasos de las actividades (importación por niveles).
  importar_siembras: "Siembras",
  // BDSJEA-465: paso extra del nivel «siembras».
  importar_stock_agricola: "Stock agrícola",
  importar_ganaderia: "Ganadería",
  importar_tambo: "Tambo",
  limpiar_siembras: "Limpieza de siembras",
  limpiar_ganaderia: "Limpieza de ganadería",
  limpiar_tambo: "Limpieza de tambo",
  // BDSJEA-454: etapa final opcional de la importación.
  recalcular_scoring: "Recalcular scoring",
}

// Mapeo por id con respaldo al título del backend; tolera pasos sin id.
export const nombrePaso = paso => {
  if (!paso || typeof paso !== "object") return "Etapa"
  return (
    NOMBRES_CORTOS[paso.id] ||
    paso.titulo ||
    paso.id ||
    (paso.orden != null ? `Etapa ${paso.orden}` : "Etapa")
  )
}

const esNumero = n => typeof n === "number" && Number.isFinite(n)

// "Productores: 3.200 de 17.017" (o solo el nombre si no se conoce el total).
export const textoEtapa = paso => {
  const nombre = nombrePaso(paso)
  if (esNumero(paso?.filasTotal) && esNumero(paso?.filasProcesadas)) {
    // BDSJEA-353: la limpieza cuenta tablas, no filas.
    const unidad = paso.unidad === "tablas" ? " tablas" : ""
    return `${nombre}: ${formatearNumero(paso.filasProcesadas)} de ${formatearNumero(paso.filasTotal)}${unidad}`
  }
  return nombre
}

// Porcentaje del paso; null = indeterminado (limpiezas o total aún desconocido).
export const porcentajePaso = paso => {
  if (esNumero(paso?.pct)) return Math.min(100, Math.max(0, paso.pct))
  if (esNumero(paso?.filasTotal) && paso.filasTotal > 0) {
    const p = ((paso.filasProcesadas || 0) / paso.filasTotal) * 100
    return Math.min(100, Math.max(0, p))
  }
  return null
}

export const formatearPorcentaje = pct =>
  esNumero(pct) ? `${formatearNumero(pct, 1)} %` : "-"

// mm:ss (o h:mm:ss a partir de la hora). null/invalid => "-".
export const formatearDuracion = seg => {
  if (!esNumero(seg) || seg < 0) return "-"
  const total = Math.floor(seg)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const dos = n => String(n).padStart(2, "0")
  return h > 0 ? `${h}:${dos(m)}:${dos(s)}` : `${dos(m)}:${dos(s)}`
}

// "hace 12 s" / "hace 2 min" para la señal de vida.
export const textoHace = seg => {
  if (!esNumero(seg) || seg < 0) return "-"
  if (seg < 60) return `hace ${formatearNumero(Math.floor(seg))} s`
  return `hace ${formatearNumero(Math.floor(seg / 60))} min`
}

export const MOTIVOS_EXCLUIDO = {
  REPETIDO_OTRA_ENTIDAD: "Repetido en otra entidad",
  REPETIDO_MISMA_ENTIDAD: "Repetido en la misma entidad",
  SIN_RAZON_SOCIAL: "Sin razón social",
  CUIT_NO_CARGADO: "CUIT no cargado",
}

// Clave desconocida => se muestra tal cual (no rompe con motivos nuevos).
export const etiquetaMotivo = clave => MOTIVOS_EXCLUIDO[clave] || clave

export const ESTADO_PASO = {
  PENDIENTE: {
    icono: "mdi-clock-outline",
    color: "text-muted",
    texto: "Pendiente",
  },
  EN_CURSO: {
    icono: "mdi-progress-clock",
    color: "text-primary",
    texto: "En curso",
  },
  OK: {
    icono: "mdi-check-circle-outline",
    color: "text-success",
    texto: "Completado",
  },
  ERROR: {
    icono: "mdi-close-circle-outline",
    color: "text-danger",
    texto: "Con error",
  },
  OMITIDO: {
    icono: "mdi-minus-circle-outline",
    color: "text-muted",
    texto: "Omitido",
  },
  // BDSJEA-441: NUNCA lo persiste el migrador; el front lo deriva (corridas por
  // niveles) para el nivel que no tuvo ningún paso.
  NO_EJECUTADO: {
    icono: "mdi-circle-off-outline",
    color: "text-muted",
    texto: "No ejecutado",
  },
}

export const infoEstadoPaso = estado =>
  ESTADO_PASO[estado] || {
    icono: "mdi-help-circle-outline",
    color: "text-muted",
    texto: estado || "-",
  }

// Motivo por el que el backend omitió un paso (`pasos[].motivoOmision`).
export const motivoPaso = paso =>
  typeof paso?.motivoOmision === "string" && paso.motivoOmision.trim()
    ? paso.motivoOmision
    : null

// true si el estado confirma la exclusión de ARCA: parametros.excluirArca === true
// o el paso sincronizar_arca en OMITIDO (mientras la corrida está en curso el
// paso puede seguir PENDIENTE aunque el parámetro ya esté confirmado).
export const excluirArcaReflejado = estado => {
  const pasos = Array.isArray(estado?.pasos) ? estado.pasos : []
  const paso = pasos.find(p => p?.id === "sincronizar_arca")
  return estado?.parametros?.excluirArca === true || paso?.estado === "OMITIDO"
}

// BDSJEA-454: el recálculo de scoring se cortó (error.paso del estado). La
// importación ya se completó: volver a lanzarla desde la pantalla REIMPORTA todo,
// por eso el front no ofrece ese reintento y explica cómo se continúa.
export const recalculoScoringCortado = estado =>
  estado?.error?.paso === "recalcular_scoring" &&
  (estado?.estadoEfectivo === "ERROR" || estado?.estado === "ERROR")

export const textoContinuarScoring = corrida =>
  `La importación ya se completó. El equipo técnico continúa con los pendientes del recálculo${
    corrida ? ` (corrida ${corrida})` : ""
  }. Volver a lanzar la importación reimporta todo.`

export const TEXTOS = {
  iniciando: "Iniciando importación...",
  exito: "Importación finalizada correctamente.",
  sinAvance:
    "El proceso está activo pero no registra avance desde hace un rato. Puede estar procesando un registro lento (ARCA o geolocalización). Si persiste, avise a soporte.",
  sinRespuesta:
    "El proceso de importación dejó de responder. Verifique el servicio de migración.",
  sinArranque:
    "El proceso de importación no generó estado. Verifique el servicio de migración.",
  errorGenerico: "La importación terminó con errores.",
  desconocido: "No se pudo determinar el estado de la importación.",
  sinNoticias: `No se recibió información del proceso en ${UMBRAL_SIN_NOTICIAS_SEG} segundos. Verifique el servicio de migración o consulte el historial.`,
  sseCortado:
    "Se perdió la conexión de la salida en vivo; el progreso se sigue consultando y la importación continúa en el servidor.",
  sseSinResultado:
    "La salida en vivo se interrumpió; la importación sigue y se consulta su estado real.",
  consultaFallida: "No se pudo consultar el estado de la importación",
  sigueEnServidor:
    "Dejaste de seguir la importación, pero continúa ejecutándose en el servidor. Consultá el historial para ver el resultado.",
  falloGenerico: "No se pudo completar la importación.",
  // BDSJEA-357: monitor.degradado=true (EN_CURSO con latido fresco pero el
  // seguimiento no se confirma). Sin infraestructura en el texto.
  seguimientoDegradado:
    "El seguimiento en vivo está degradado; la importación sigue en curso. Se vuelve a consultar automáticamente.",
  // Se pidió excluir ARCA pero el estado de la corrida no lo refleja.
  excluirArcaNoReflejado:
    "Se pidió excluir ARCA, pero el estado de la corrida no confirma que se haya omitido. Puede que se consulte ARCA igual.",
}

// BDSJEA-357: código estable que el backend incluye en el mensaje del evento
// SSE `error` cuando se corta solo el stream (la importación sigue corriendo).
// DEBE coincidir con MigracionService.CODIGO_SEGUIMIENTO_PERDIDO (ewan-agro,
// ewan-agro/.../service/MigracionService.java) y con migracion-bsj/CONTRATO_ESTADO.md:
// si el backend cambia el código, hay que cambiarlo acá también (si no, el
// front vuelve en silencio al camino previo: IDLE + mensaje duplicado).
export const CODIGO_SEGUIMIENTO_PERDIDO = "MIGRACION_SEGUIMIENTO_PERDIDO"

export const esSeguimientoPerdido = msg =>
  typeof msg === "string" && msg.includes(CODIGO_SEGUIMIENTO_PERDIDO)

// BDSJEA-353: bloque `limpiezaCompleta` del estado (contrato de migracion-bsj/
// CONTRATO_ESTADO.md). `modo` es REAL | ENSAYO; en ENSAYO `filasBorradas` es lo
// que se borraria (todo termina en ROLLBACK). Otro valor => null (no se muestra).
export const modoLimpieza = bloque => {
  const modo = bloque && typeof bloque === "object" ? bloque.modo : null
  if (modo === "REAL") return "Real"
  if (modo === "ENSAYO") return "Ensayo (no se borró nada)"
  return null
}

const ESTADOS_LIMPIEZA = {
  PENDIENTE: "Pendiente",
  EN_CURSO: "En curso",
  OK: "Completada",
  GUARDA: "Detenida por la guarda (no se borró nada)",
  PREFLIGHT: "Detenida en la verificación previa (no se borró nada)",
  REHABILITACION_PENDIENTE: "Borrado aplicado; restricciones incompletas",
  INCIERTO: "Resultado incierto",
  INTERRUMPIDA: "Interrumpida",
  ERROR: "Con error",
}

// Estado desconocido => se muestra tal cual (no rompe con valores nuevos).
export const etiquetaEstadoLimpieza = estado =>
  ESTADOS_LIMPIEZA[estado] || estado

export const TEXTO_BORRADO_INCIERTO =
  "No se pudo confirmar si se borró: verificá el estado de la base antes de importar."

export const textoExitoConExcluidos = excluidos =>
  `Importación finalizada. ${formatearNumero(excluidos)} ${
    excluidos === 1
      ? "cliente excluido registrado"
      : "clientes excluidos registrados"
  } en el archivo de excluidos (ver abajo).`

// Color del Alert/Badge según el estado efectivo.
export const colorEstado = estadoEfectivo => {
  switch (estadoEfectivo) {
    case ESTADO.COMPLETADA:
    case ESTADO.COMPLETADA_CON_EXCLUIDOS:
      return "success"
    case ESTADO.ERROR:
    case ESTADO.INTERRUMPIDA:
    case ESTADO.SIN_RESPUESTA:
    case ESTADO.SIN_ARRANQUE:
      return "danger"
    case ESTADO.DESCONOCIDO:
      return "warning"
    case ESTADO.EN_CURSO:
      return "primary"
    default:
      return "info"
  }
}

// Texto corto del badge de estado.
export const rotuloEstado = estadoEfectivo =>
  ({
    INICIANDO: "Iniciando",
    EN_CURSO: "En curso",
    COMPLETADA: "Completada",
    COMPLETADA_CON_EXCLUIDOS: "Completada con excluidos",
    ERROR: "Con error",
    INTERRUMPIDA: "Interrumpida",
    SIN_RESPUESTA: "Sin respuesta",
    SIN_ARRANQUE: "Sin arranque",
    DESCONOCIDO: "Desconocido",
  })[estadoEfectivo] || "Iniciando"

// ---------------------------------------------------------------------------
// Historial de importaciones: clasificacion de filas de los CSV de resultado.
// ---------------------------------------------------------------------------

// Resultados que cuentan como "importado" (no son un faltante a revisar).
const RESULTADOS_OK = ["CREADO", "ACTUALIZADO", "YA_EXISTIA"]
// En establecimientos SALTADO_EXISTENTE tampoco es un faltante (ya existia).
const RESULTADOS_OK_ESTABLECIMIENTOS = [...RESULTADOS_OK, "SALTADO_EXISTENTE"]
// BDSJEA-441: en las actividades también son importados CREADO_HISTORIA (tambo)
// y SALTADO_EXISTENTE (ya existía por clave de origen).
const RESULTADOS_OK_ACTIVIDADES = [
  ...RESULTADOS_OK,
  "CREADO_HISTORIA",
  "SALTADO_EXISTENTE",
]
const TIPOS_ACTIVIDAD = ["siembras", "ganaderia", "tambo", "stockAgricola"]

const ETIQUETAS_RESULTADO = {
  CREADO: "Creados",
  CREADO_HISTORIA: "Creados (historia)",
  ACTUALIZADO: "Actualizados",
  YA_EXISTIA: "Ya existían",
  REPETIDO_OTRA_ENTIDAD: MOTIVOS_EXCLUIDO.REPETIDO_OTRA_ENTIDAD,
  REPETIDO_MISMA_ENTIDAD: MOTIVOS_EXCLUIDO.REPETIDO_MISMA_ENTIDAD,
  DESCARTADO: "Descartado",
  ERROR: "Error",
  SALTADO_EXISTENTE: "Saltado (ya existía)",
  SINCRONIZADO: "Sincronizados",
  SIN_DATOS: "Sin datos",
}

// Clave desconocida => "Otro" (nunca el texto crudo: podria ser texto libre/personal si el CSV llega
// desplazado); vacia => "Sin resultado".
export const etiquetaResultado = resultado =>
  ETIQUETAS_RESULTADO[resultado] || (resultado ? "Otro" : "Sin resultado")

// Etiqueta del resultado de UNA FILA (tablas): en singular ("Creado"), a
// diferencia de las tarjetas y graficos, que cuentan filas y van en plural.
const ETIQUETAS_RESULTADO_FILA = {
  ...ETIQUETAS_RESULTADO,
  CREADO: "Creado",
  CREADO_HISTORIA: "Creado (historia)",
  ACTUALIZADO: "Actualizado",
  YA_EXISTIA: "Ya existía",
  SINCRONIZADO: "Sincronizado",
}

export const etiquetaResultadoFila = resultado =>
  ETIQUETAS_RESULTADO_FILA[resultado] || (resultado ? "Otro" : "Sin resultado")

/**
 * Clasifica el `resultado` de una fila. tipo: "productores" | "establecimientos"
 * | "siembras" | "ganaderia" | "tambo". noImportado: todo lo que no es
 * CREADO/ACTUALIZADO/YA_EXISTIA (en establecimientos tampoco SALTADO_EXISTENTE;
 * en las actividades tampoco CREADO_HISTORIA ni SALTADO_EXISTENTE). Todo no importado cuenta
 * tambien como "a revisar" (ERROR, DESCARTADO, REPETIDO_*, y cualquier
 * resultado nuevo o vacio: ante la duda se muestra).
 */
export const clasificarResultado = (resultado, tipo) => {
  let ok = RESULTADOS_OK
  if (tipo === "establecimientos") ok = RESULTADOS_OK_ESTABLECIMIENTOS
  else if (TIPOS_ACTIVIDAD.includes(tipo)) ok = RESULTADOS_OK_ACTIVIDADES
  const noImportado = !ok.includes(resultado)
  return {
    noImportado,
    aRevisar: noImportado,
    etiqueta: etiquetaResultado(resultado),
  }
}

/**
 * Los ids llegan del CSV como texto de pandas ("10147.0"). Devuelve el numero
 * entero o null si esta vacio o no es un id ("1.5" no lo es).
 */
export const normalizarId = valor => {
  if (typeof valor === "number") {
    return Number.isInteger(valor) ? valor : null
  }
  if (typeof valor !== "string") return null
  const texto = valor.trim()
  return /^\d+(\.0+)?$/.test(texto) ? Number(texto.split(".")[0]) : null
}

// [{id, codigo}] (catalogo de entidades) => {id: codigo}. Tolera cualquier entrada.
export const construirMapaEntidades = lista => {
  const mapa = {}
  if (!Array.isArray(lista)) return mapa
  lista.forEach(e => {
    const id = normalizarId(e?.id)
    if (id != null && e?.codigo) mapa[id] = e.codigo
  })
  return mapa
}

/**
 * Reemplaza "entidad N" por "entidad <codigo>" segun el catalogo ({id: codigo}).
 * Si el id no esta en el mapa (o no hay mapa) deja el texto igual.
 */
export const reemplazarEntidadPorCodigo = (texto, mapa) => {
  if (typeof texto !== "string" || !mapa || typeof mapa !== "object") {
    return texto
  }
  return texto.replace(/\b(entidad)\s+(\d+)/gi, (completo, palabra, id) => {
    const codigo = mapa[Number(id)]
    return codigo ? `${palabra} ${codigo}` : completo
  })
}

// ---------------------------------------------------------------------------
// Fechas de la migracion: SIEMPRE en ART (America/Argentina/Buenos_Aires,
// UTC-3, sin horario de verano), nunca UTC ni la zona del navegador.
//
// Origen de los timestamps (el migrador corre en UTC; Ewan-agro pasa los textos
// sin tocar):
//  - Con huso (offset "+00:00" o "Z"): estado.inicio/actualizado/ultimoAvance/
//    fin, pasos[].inicio/fin, limpiezaCompleta.inicio/fin y
//    puntoRestauracionUtc. Se convierten desde ese instante.
//  - SIN huso (UTC del contenedor del migrador): nombre de carpeta
//    migracion-AAAA-MM-DD_HH-MM-SS, `inicio` de /status ("aaaa-MM-dd HH:mm:ss"),
//    fechas de resumen_migracion.log e inicio/actualizado de progreso_*.json.
//    Se interpretan como UTC y se convierten a ART.
// Una misma utilidad resuelve ambos casos para no convertir dos veces ni
// mezclar husos: un valor con huso nunca se vuelve a desplazar.
// ---------------------------------------------------------------------------

const ZONA_ART = "America/Argentina/Buenos_Aires"

const FORMATO_ART = new Intl.DateTimeFormat("en-US", {
  timeZone: ZONA_ART,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
})

// aaaa-MM-dd[ T]HH:mm[:ss[.fff]] + huso opcional (Z, +HH, +HH:MM, +HHMM).
const PATRON_ISO =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,]\d+)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i
// migracion-aaaa-MM-dd_HH-mm-ss (hora UTC del contenedor del migrador).
const PATRON_CARPETA =
  /(?:^|\/|\\)migracion-(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})$/

const aMilisUtc = (a, mes, d, h, mi, s) => {
  const [anio, mm, dd, hh, min, seg] = [a, mes, d, h, mi, s].map(Number)
  const ms = Date.UTC(anio, mm - 1, dd, hh, min, seg || 0)
  const f = new Date(ms)
  // Rechaza fechas imposibles (31/02, hora 25) que Date.UTC "corrige".
  return f.getUTCFullYear() === anio &&
    f.getUTCMonth() === mm - 1 &&
    f.getUTCDate() === dd &&
    f.getUTCHours() === hh &&
    f.getUTCMinutes() === min &&
    f.getUTCSeconds() === (seg || 0)
    ? ms
    : null
}

const desplazamientoMin = huso => {
  if (!huso || huso.toUpperCase() === "Z") return 0
  const signo = huso[0] === "-" ? -1 : 1
  const digitos = huso.slice(1).replace(":", "")
  const h = Number(digitos.slice(0, 2))
  const m = Number(digitos.slice(2, 4) || 0)
  return signo * (h * 60 + m)
}

// Instante (ms epoch) de un valor; null si esta vacio o no se entiende.
export const instanteDe = valor => {
  if (valor instanceof Date) {
    return Number.isNaN(valor.getTime()) ? null : valor.getTime()
  }
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null
  if (typeof valor !== "string") return null
  const texto = valor.trim()
  const carpeta = PATRON_CARPETA.exec(texto)
  if (carpeta) return aMilisUtc(...carpeta.slice(1, 7))
  const iso = PATRON_ISO.exec(texto)
  if (!iso) return null
  const base = aMilisUtc(...iso.slice(1, 7))
  if (base == null) return null
  // Con huso se respeta; sin huso es UTC del contenedor del migrador.
  return base - desplazamientoMin(iso[7]) * 60000
}

/**
 * Regla de rotulado: todo se muestra en ART y la hora ART NO lleva rotulo.
 * Solo una hora que NO es ART (UTC u otra zona) se rotula con su zona.
 * `zona` es la zona IANA en la que se quiere mostrar (por defecto ART);
 * "UTC" => "dd/MM/aaaa HH:mm:ss UTC". Vacio o invalido => "-".
 */
export const formatearFechaEnZona = (valor, zona = ZONA_ART) => {
  const ms = instanteDe(valor)
  if (ms == null) return "-"
  const esArt = zona === ZONA_ART
  const formato = esArt
    ? FORMATO_ART
    : new Intl.DateTimeFormat("en-US", {
        timeZone: zona,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
  const partes = {}
  formato.formatToParts(new Date(ms)).forEach(p => {
    partes[p.type] = p.value
  })
  const texto = `${partes.day}/${partes.month}/${partes.year} ${partes.hour}:${partes.minute}:${partes.second}`
  return esArt ? texto : `${texto} ${zona === "UTC" ? "UTC" : zona}`
}

/**
 * "dd/MM/aaaa HH:mm:ss" (en ART, sin rotulo) para un ISO (con o sin huso), un
 * nombre de carpeta migracion-AAAA-MM-DD_HH-MM-SS, un Date o un epoch.
 * Vacio o invalido => "-".
 */
export const formatearFechaART = valor => formatearFechaEnZona(valor, ZONA_ART)

// Etiqueta de una corrida: la fecha en ART si el nombre es una carpeta
// migracion-AAAA-MM-DD_HH-MM-SS; otro nombre se muestra tal cual. El valor
// (nombre de carpeta) nunca cambia: solo cambia lo que se ve.
export const etiquetaCorrida = nombre => {
  const fecha = PATRON_CARPETA.test(String(nombre ?? "").trim())
    ? formatearFechaART(nombre)
    : "-"
  return fecha === "-" ? nombre : fecha
}

// Convierte a ART las fechas "aaaa-MM-dd HH:mm:ss" (sin huso: UTC) y las
// carpetas migracion-... que aparezcan dentro de un texto libre (resumen).
export const convertirFechasEnTextoART = texto => {
  if (typeof texto !== "string") return texto
  return texto.replace(
    /migracion-\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}|\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g,
    coincidencia => {
      const f = formatearFechaART(coincidencia)
      return f === "-" ? coincidencia : f
    },
  )
}

// ---------------------------------------------------------------------------
// Categorias de los graficos del historial.
// Un grafico NUNCA agrupa por texto libre ni por datos personales: solo por
// `resultado` y solo con valores de un conjunto CERRADO. Cualquier otro valor
// (por ejemplo un fragmento de razon social por un CSV mal parseado) cae en
// "Otros", y hay un tope de categorias visibles.
// ---------------------------------------------------------------------------

export const MAX_CATEGORIAS_GRAFICO = 8
export const CATEGORIA_OTROS = "OTROS"
export const CATEGORIA_SIN_RESULTADO = "DESCONOCIDO"

// Conjunto cerrado de resultados graficables (los que escriben los scripts de migracion).
export const RESULTADOS_GRAFICABLES = Object.freeze(
  Object.keys(ETIQUETAS_RESULTADO),
)

/**
 * Cuenta filas por `resultado` (una pasada) y devuelve [{name, value, etiqueta}]
 * de mayor a menor: solo claves de RESULTADOS_GRAFICABLES (+ "Sin resultado"
 * para vacio), maximo `max` categorias; el resto se suma en "Otros".
 */
export const categorizarResultados = (filas, max = MAX_CATEGORIAS_GRAFICO) => {
  const cerrado = new Set(RESULTADOS_GRAFICABLES)
  const counts = {}
  let otros = 0
  filas.forEach(f => {
    const r = f && typeof f.resultado === "string" ? f.resultado.trim() : ""
    if (!r)
      counts[CATEGORIA_SIN_RESULTADO] =
        (counts[CATEGORIA_SIN_RESULTADO] || 0) + 1
    else if (cerrado.has(r)) counts[r] = (counts[r] || 0) + 1
    else otros += 1
  })
  const ordenadas = Object.entries(counts).sort((a, b) => b[1] - a[1])
  const visibles = ordenadas.slice(0, Math.max(max - 1, 1))
  otros += ordenadas.slice(visibles.length).reduce((s, [, v]) => s + v, 0)
  const resultado = visibles.map(([name, value]) => ({
    name,
    value,
    etiqueta: etiquetaResultado(name === CATEGORIA_SIN_RESULTADO ? "" : name),
  }))
  if (otros > 0)
    resultado.push({ name: CATEGORIA_OTROS, value: otros, etiqueta: "Otros" })
  return resultado
}

// ---------------------------------------------------------------------------
// BDSJEA-468: celebracion (fuegos artificiales) al terminar una importacion.
// ---------------------------------------------------------------------------

// Estados terminales que cuentan como importacion EXITOSA. COMPLETADA_CON_EXCLUIDOS
// (clientes excluidos registrados en el CSV de excluidos) es un exito: los excluidos
// son una decision del importador, no una falla. Si cambia el criterio, es aca.
export const ESTADOS_EXITO_IMPORTACION = [
  ESTADO.COMPLETADA,
  ESTADO.COMPLETADA_CON_EXCLUIDOS,
]

export const TEXTO_EXITO_IMPORTACION = "Importación exitosa"

/**
 * true si la corrida terminó bien: estado terminal de éxito, sin bloque `error`
 * y sin ningún paso en ERROR. (ERROR / INTERRUMPIDA / abortadas nunca son éxito.)
 */
export const esImportacionExitosa = estado => {
  if (!estado || typeof estado !== "object") return false
  if (!ESTADOS_EXITO_IMPORTACION.includes(estado.estadoEfectivo)) return false
  if (estado.error) return false
  const pasos = Array.isArray(estado.pasos) ? estado.pasos : []
  return !pasos.some(p => p?.estado === "ERROR")
}
